import React, { useState, useEffect, useCallback, useRef } from "react";
import { ISAIAH_POSES, SIZE_VARIANTS, POSE_ANIMATIONS } from "./IsaiahAnimationLibrary";
import { SmartImg } from "../../lib/smartImg";
import "./isaiah-animations.css";

/**
 * IsaiahMascotCore v8, Single Image, CSS Motion States
 *
 * Displays ONE consistent image of Isaiah (idle.png, always the same character).
 * Different "poses" are expressed via CSS transforms/animations, NOT different images.
 * This guarantees 100% character consistency while giving visual variety per world.
 *
 * motionState prop applies world-specific CSS classes:
 *   "hype" → isaiah-motion-hype (arcade bouncing)
 *   "exploring" → isaiah-motion-exploring (adventure lean)
 *   "crafting" → isaiah-motion-crafting (builder focus)
 */

const CONFETTI_COLORS = ["#FBBF24", "#A78BFA", "#34D399", "#F472B6", "#38BDF8", "#FB923C"];

// Maps motionState values to CSS class names
const MOTION_STATE_CLASSES = {
  idle: "isaiah-motion-idle",
  greeting: "isaiah-motion-greeting",
  looking: "isaiah-motion-looking",
  pondering: "isaiah-motion-pondering",
  excited: "isaiah-motion-excited",
  hype: "isaiah-motion-hype",
  exploring: "isaiah-motion-exploring",
  crafting: "isaiah-motion-crafting",
  dancing: "isaiah-motion-dancing",
};

export const IsaiahMascotCore = ({
  pose = "idle",
  size = "full-body",
  entrance = null,
  glow = false,
  breathing = true,
  className = "",
  style = {},
  onClick,
  testId = "isaiah-mascot",
  danceable = false,
  overrideImage = null,
  motionState = null,
  // Legacy props, accepted but ignored
  sequence = null,
  onMotionChange = null,
}) => {
  const [entered, setEntered] = useState(!entrance);
  const [confetti, setConfetti] = useState([]);
  const confettiIdRef = useRef(0);

  // Entrance animation
  useEffect(() => {
    if (entrance) {
      const t = setTimeout(() => setEntered(true), 50);
      return () => clearTimeout(t);
    }
  }, [entrance]);

  // Confetti burst on click
  const spawnConfetti = useCallback(() => {
    const particles = Array.from({ length: 14 }, () => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 50 + Math.random() * 70;
      return {
        id: ++confettiIdRef.current,
        x: 45 + Math.random() * 10,
        y: 25 + Math.random() * 15,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        endX: Math.cos(angle) * distance,
        endY: Math.sin(angle) * distance - 30,
        size: 4 + Math.random() * 5,
        rotation: Math.random() * 360,
        delay: Math.random() * 0.15,
        type: Math.random() > 0.5 ? "circle" : "rect",
      };
    });
    setConfetti(particles);
    setTimeout(() => setConfetti([]), 1200);
  }, []);

  const handleClick = useCallback((e) => {
    if (danceable) spawnConfetti();
    onClick?.(e);
  }, [danceable, spawnConfetti, onClick]);

  const dimensions = SIZE_VARIANTS[size] || SIZE_VARIANTS["full-body"];
  // Use override image if provided (world outfits), otherwise fall back to pose image
  const imageSrc = overrideImage || ISAIAH_POSES[pose] || ISAIAH_POSES.idle;

  // Resolve CSS animation class: motionState takes priority, then pose animation
  const motionClass = motionState
    ? (MOTION_STATE_CLASSES[motionState] || "")
    : (POSE_ANIMATIONS[pose] || "");

  const entranceClass = entrance && !entered ? "opacity-0" :
    entrance && entered ? `isaiah-entrance-${entrance === "slideRight" ? "rise" : entrance}` : "";

  return (
    <div
      className={[
        "isaiah-mascot-core relative inline-block select-none",
        entranceClass,
        (onClick || danceable) ? "cursor-pointer" : "",
        className,
      ].filter(Boolean).join(" ")}
      style={{ width: dimensions.width, height: dimensions.height, background: "none", ...style }}
      onClick={handleClick}
      data-testid={testId}
    >
      {glow && (
        <div
          className="absolute pointer-events-none isaiah-glow"
          style={{
            top: "10%", left: "5%", right: "5%", bottom: "0",
            background: "radial-gradient(ellipse at 50% 40%, rgba(59,130,246,0.2), rgba(139,92,246,0.08) 45%, transparent 75%)",
            filter: "blur(24px)", zIndex: -1,
          }}
        />
      )}

      {/* Float layer */}
      <div className="w-full h-full isaiah-layer-float">
        {/* Breathe layer */}
        <div className={`w-full h-full ${breathing ? "isaiah-layer-breathe" : ""}`}>
          {/* Motion state layer, CSS transforms create pose illusion on same image */}
          <div className={`w-full h-full ${motionClass}`}>
            <SmartImg
              src={imageSrc}
              alt="Isaiah"
              className="w-full h-full object-contain"
              draggable={false}
              style={{
                background: "transparent",
                filter: glow ? "drop-shadow(0 4px 16px rgba(59,130,246,0.3))" : "none",
              }}
            />
          </div>
        </div>
      </div>

      {/* Confetti on click */}
      {confetti.length > 0 && (
        <div className="absolute inset-0 pointer-events-none overflow-visible" data-testid="isaiah-confetti">
          {confetti.map(p => (
            <div
              key={p.id}
              className="absolute isaiah-confetti-particle"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: p.size,
                height: p.type === "rect" ? p.size * 0.6 : p.size,
                borderRadius: p.type === "circle" ? "50%" : "2px",
                background: p.color,
                boxShadow: `0 0 ${p.size}px ${p.color}80`,
                animationDelay: `${p.delay}s`,
                "--end-x": `${p.endX}px`,
                "--end-y": `${p.endY}px`,
                "--spin": `${p.rotation}deg`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default IsaiahMascotCore;
